import React from 'react';
import { Box, Typography } from '@mui/material';

interface TeamRow {
  position: number | string;
  teamName: string;
  won: number;
  drawn: number;
  lost: number;
  points: number;
  played: number;
  isUserTeam?: boolean;
}

interface StandingsBarListProps {
  teams: TeamRow[];
  standingsSortBy?: string;
}

const StandingsBarList: React.FC<StandingsBarListProps> = ({ teams, standingsSortBy }) => {
  const isPPG = standingsSortBy === 'POINTS_PER_GAME';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      {teams.map((team) => {
        const total = team.played || 1;
        const winPct = (team.won / total) * 100;
        const drawPct = (team.drawn / total) * 100;
        const lossPct = (team.lost / total) * 100;
        const displayValue = isPPG
          ? (!team.played || team.played === 0 ? '0.00' : (team.points / team.played).toFixed(2))
          : team.points;
        const label = isPPG ? 'ppg' : 'pts';

        return (
          <Box
            key={team.teamName}
            sx={{
              px: 1.5,
              py: 1,
              borderRadius: 1,
              ...(team.isUserTeam && {
                bgcolor: 'rgba(76,175,80,0.08)',
                border: '1px solid rgba(76,175,80,0.2)',
              }),
            }}
          >
            {/* Top row: position, team name, W/D/L, points */}
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, mb: 0.5 }}>
              <Typography
                sx={{
                  minWidth: 24,
                  textAlign: 'center',
                  color: 'text.secondary',
                  fontWeight: team.isUserTeam ? 700 : 400,
                  fontSize: '0.85rem',
                  lineHeight: 1.3,
                  alignSelf: 'center',
                }}
              >
                {team.position}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  fontWeight: team.isUserTeam ? 700 : 400,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: '0.85rem',
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  display: { xs: '-webkit-box', sm: 'block' },
                  WebkitLineClamp: { xs: 2, sm: 'unset' },
                  WebkitBoxOrient: 'vertical',
                  lineHeight: 1.3,
                  wordBreak: 'break-word',
                  mr: { xs: 1, sm: 0 },
                }}
              >
                {team.teamName}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 600, fontSize: '0.7rem' }}>
                  {team.won}W
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.7rem' }}>
                  {team.drawn}D
                </Typography>
                <Typography variant="caption" sx={{ color: '#F57C00', fontWeight: 600, fontSize: '0.7rem' }}>
                  {team.lost}L
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.4 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, fontSize: '0.85rem' }}
                  >
                    {displayValue}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', fontSize: '0.65rem' }}
                  >
                    {label}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Stacked bar */}
            <Box
              sx={{
                display: 'flex',
                height: 8,
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: 'rgba(0,0,0,0.06)',
              }}
            >
              {team.won > 0 && (
                <Box sx={{ width: `${winPct}%`, bgcolor: '#A5D6A7', transition: 'width 0.3s' }} />
              )}
              {team.drawn > 0 && (
                <Box sx={{ width: `${drawPct}%`, bgcolor: '#E0E0E0', transition: 'width 0.3s' }} />
              )}
              {team.lost > 0 && (
                <Box sx={{ width: `${lossPct}%`, bgcolor: '#FFCC80', transition: 'width 0.3s' }} />
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default StandingsBarList;
